import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import MarketOverview from './components/MarketOverview';
import PriceChart from './components/PriceChart';
import AuctionCalendar from './components/AuctionCalendar';
import MarketAlerts from './components/MarketAlerts';
import ComparativeAnalysis from './components/ComparativeAnalysis';

const MarketAnalysis = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const tabs = [
    { id: 'overview', label: 'Market Overview', icon: 'BarChart3' },
    { id: 'trends', label: 'Price Trends', icon: 'TrendingUp' },
    { id: 'auctions', label: 'Auction Calendar', icon: 'Calendar' },
    { id: 'alerts', label: 'Market Alerts', icon: 'Bell' },
    { id: 'compare', label: 'Compare Items', icon: 'GitCompare' }
  ];

  const marketSummary = {
    totalValue: "$2.4B",
    dailyChange: "+2.3%",
    activeAuctions: 1247,
    topGainer: "Vintage Watches (+5.2%)"
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <MarketOverview />;
      case 'trends':
        return <PriceChart />;
      case 'auctions':
        return <AuctionCalendar />;
      case 'alerts':
        return <MarketAlerts />;
      case 'compare':
        return <ComparativeAnalysis />;
      default:
        return <MarketOverview />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <main className={`pt-16 transition-all duration-300 ${
        sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
      }`}>
        <div className="p-4 sm:p-6 lg:p-8">
          {/* Page Header */}
          <div className="mb-6 sm:mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-foreground font-accent">Market Intelligence</h1>
                <p className="text-muted-foreground mt-1 sm:mt-2 text-sm sm:text-base">
                  Real-time market data, trends, and investment insights for premium collectibles
                </p>
              </div>
              
              <div className="flex items-center gap-3 flex-wrap">
                {/* Market Summary Cards */}
                <div className="hidden xl:flex items-center gap-3">
                  <div className="bg-card border border-border rounded-lg px-4 py-3">
                    <div className="flex items-center space-x-2">
                      <Icon name="DollarSign" size={16} className="text-success" />
                      <div>
                        <p className="text-sm font-medium text-card-foreground">{marketSummary?.totalValue}</p>
                        <p className="text-xs text-muted-foreground">Total Market</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-card border border-border rounded-lg px-4 py-3">
                    <div className="flex items-center space-x-2">
                      <Icon name="TrendingUp" size={16} className="text-success" />
                      <div>
                        <p className="text-sm font-medium text-success">{marketSummary?.dailyChange}</p>
                        <p className="text-xs text-muted-foreground">24h Change</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Refresh Button */}
                <button className="flex items-center gap-2 px-4 py-2.5 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors duration-200 premium-hover text-sm min-h-[44px]">
                  <Icon name="RefreshCw" size={16} />
                  <span className="font-medium">Refresh Data</span>
                </button>
              </div>
            </div>

            {/* Live Market Status */}
            <div className="mt-4 sm:mt-6 flex items-center gap-4 sm:gap-6 text-sm flex-wrap">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
                <span className="text-muted-foreground">Market Open</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={14} className="text-muted-foreground" />
                <span className="text-muted-foreground">Last updated: 2 minutes ago</span>
              </div>
              <div className="hidden sm:flex items-center space-x-2">
                <Icon name="Activity" size={14} className="text-muted-foreground" />
                <span className="text-muted-foreground">{marketSummary?.activeAuctions} active auctions</span>
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="mb-6 sm:mb-8">
            <div className="border-b border-border">
              <nav className="flex gap-2 sm:gap-4 lg:gap-8 overflow-x-auto scrollbar-hide pb-px">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-1.5 sm:gap-2 py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm transition-colors duration-200 whitespace-nowrap min-h-[48px] ${
                      activeTab === tab?.id
                        ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                    }`}
                  >
                    <Icon name={tab?.icon} size={16} />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="animate-fade-in">
            {renderTabContent()}
          </div>

          {/* Quick Actions Footer */}
          <div className="mt-8 sm:mt-12 bg-card border border-border rounded-lg p-4 sm:p-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-card-foreground">Market Intelligence Tools</h3>
                <p className="text-sm text-muted-foreground">
                  Access advanced analytics and market research tools
                </p>
              </div>
              
              <div className="flex flex-wrap gap-3">
                <button className="flex items-center space-x-2 px-4 py-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors duration-200 premium-hover">
                  <Icon name="Calculator" size={16} />
                  <span className="text-sm font-medium">Valuation Calculator</span>
                </button>
                
                <button className="flex items-center space-x-2 px-4 py-2 bg-muted/50 text-muted-foreground rounded-lg hover:bg-muted hover:text-card-foreground transition-colors duration-200 premium-hover">
                  <Icon name="FileText" size={16} />
                  <span className="text-sm font-medium">Market Report</span>
                </button>
                
                <button className="flex items-center space-x-2 px-4 py-2 bg-muted/50 text-muted-foreground rounded-lg hover:bg-muted hover:text-card-foreground transition-colors duration-200 premium-hover">
                  <Icon name="Share2" size={16} />
                  <span className="text-sm font-medium">Share Analysis</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MarketAnalysis;